import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Operationsanmalan = {
    storeInfo?: {
        /**
         * true om hämtning av operationsanmälan gjorts via en endpoint som bara hämtar delar av anmälan
         * (t.ex Operationskoordinering). Används för att avgöra om tillräcklig med data finns i store eller om
         * det behöver göras en "större" hämtning
         */
        partial?: boolean;
    };
    id: string;
    patient: string;
    patient_display?: {
        personId_formatted: string;
        namn: string;
        namn_reverse: string;
        onskar_sms: boolean;
    };
    prio_display?: {
        displayName: string;
    };
    current_bokad_huvudoperator_display?: {
        user_display: {
            name: string;
        };
    };
    ass_operatorer_display?: {
        user_display: {
            name: string;
        };
    }[];
    anestesi_display?: {
        displayName: string;
    };
    avtal_display?: {
        namn: string;
    };
    bokad_sal_display?: {
        namn: string;
    };
    kontaktorsak_display?: {
        displayName: string;
    };
    typoperation_display?: {
        namn: string;
    };
    huvudoperationskod_display?: {
        code: string;
        displayName: string;
    };
    aktuell_preanestesibedomning?: {
        [key: string]: unknown;
    };
    skickade_remisser?: Array<{
        mottagare_display: {
            namn: string;
        };
    }>;
    mottagna_remisser?: Array<{
        avsandare_display: {
            namn: string;
        };
    }>;
    planerad_vardform_display?: {
        id: number;
        namn: string;
        typ: string;
        typ_display: string;
    };
    operationsenhet_display?: {
        name: string;
    };
    studie_kvalitetsregister_display?: Array<{
        id: number;
        namn: string;
        ordning: number;
    }>;
    [key: string]: unknown;
};

type State = BaseState & {
    operationsanmalningar: Map<string, Operationsanmalan>;
};

type Getters = {
    operationsanmalanById: (data: { id: Operationsanmalan["id"] }) => Operationsanmalan | null;
};

const filterParamConverter = ({ filterParams }: { filterParams: FilterParams }) => {
    return Object.fromEntries(
        Object.entries(filterParams).map(([key, value]) => {
            if (key === "show_strukna") {
                return [`status${value ? "__any" : "__ne"}`, "Struken"];
            } else if (key === "patient__personId") {
                return ["patient_display__personId", value];
            }

            return [key, value];
        })
    );
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        operationsanmalningar: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "operationsanmalningar";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchoperationsanmalning)
         */
        operationsanmalningar: (state: State) => [...state.operationsanmalningar.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        operationsanmalanSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            }),
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getOperationsanmalanById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        operationsanmalanById:
            (state: State) =>
            ({ id }: { id?: Operationsanmalan["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.operationsanmalningar.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createOperationsanmalan)
         * Se helpers/subset
         */
        addOperationsanmalningar: (
            state: State,
            {
                offset,
                total,
                subsetName = "default",
                operationsanmalningar = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                operationsanmalningar: Operationsanmalan[];
                total?: number;
            }
        ) =>
            subsetSetter<State, Operationsanmalan>({
                offset,
                subsetName,
                state,
                filterParamConverter,
                total,
                collectionName: moduleName,
                items: operationsanmalningar,
            }),
        /**
         * Uppdatering av objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (updateOperationsanmalan)
         * Se helpers/subset
         */
        updateOperationsanmalningar: (
            state: State,
            {
                operationsanmalningar,
                offset,
                frozenSubsets,
                freezeAllSubsets,
            }: {
                operationsanmalningar: Operationsanmalan[];
                offset: number;
                frozenSubsets?: string[];
                freezeAllSubsets?: boolean;
            }
        ) =>
            subsetUpdateSetter<State, Operationsanmalan>({
                offset,
                state,
                filterParamConverter,
                frozenSubsets,
                freezeAllSubsets,
                collectionName: moduleName,
                items: operationsanmalningar,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getOperationsanmalanById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                addAsNew,
                addAsUpdate,
                forceRefresh,
                partial = false,
                config = {},
            }: {
                id: Operationsanmalan["id"];
                // Ska hämtade datan stoppas in i newlyCreated subset?
                addAsNew?: boolean;
                /*
                    Ska hämtningen läggas till som newlyUpdated?
                    Existerande data kommer att ignoreras i store och en ny hämtning från BE kommer att göras
                 */
                addAsUpdate?: boolean;
                config?: {
                    tabbnamn?: string;
                };
                partial: boolean;
                forceRefresh?: boolean;
            }
        ) => {
            const found = getters.operationsanmalanById({ id });

            if (!forceRefresh && !addAsUpdate && (found || !id) && partial === !!found?.storeInfo?.partial) {
                if (found && addAsNew) {
                    commit("addOperationsanmalningar", {
                        offset: 0,
                        operationsanmalningar: [found],
                        subsetName: "newlyCreated",
                    });
                }

                return found;
            }

            try {
                const { data } = await klinikenApi.get<{
                    operationsanmalan: Operationsanmalan;
                }>(`operation/anmalan/${id}`, getConfig(config));

                if (data) {
                    const anmalan = {
                        storeInfo: {
                            partial,
                        },
                        ...data,
                    };

                    if (addAsUpdate && forceRefresh) {
                        commit("updateOperationsanmalningar", {
                            subsetName: "newlyUpdated",
                            operationsanmalningar: [anmalan],
                            offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                            freezeAllSubsets: true,
                        });
                    } else {
                        commit("addOperationsanmalningar", {
                            offset: 0,
                            operationsanmalningar: [anmalan],
                            subsetName: addAsNew ? "newlyCreated" : id,
                        });

                        if (addAsUpdate) {
                            commit("updateOperationsanmalningar", {
                                subsetName: "newlyUpdated",
                                operationsanmalningar: [anmalan],
                                offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                            });
                        }
                    }

                    return anmalan;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta slutenvårdsbesök från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         */
        fetchOperationsanmalningar: async (
            { commit, state }: BaseActionsProps<State>,
            {
                forceRefresh,
                storeInfo,
                apiUrl,
                resetLocalSubsets,
                limit = 20,
                page = 1,
                filterParams = {},
                config = {},
            }: {
                limit?: number;
                offset?: number;
                page?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                forceRefresh?: boolean;
                storeInfo?: Operationsanmalan["storeInfo"];
                apiUrl?: string;
                resetLocalSubsets?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Operationsanmalan>({
                    filterParams,
                    state,
                    limit,
                    page,
                    config,
                    forceRefresh,
                    storeInfo,
                    collectionName: moduleName,
                    apiUrl: apiUrl ?? "operation/anmalan/",
                });

                if (resetLocalSubsets) {
                    commit("resetLocalSubsets");
                }

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addOperationsanmalningar", {
                    subsetName,
                    total,
                    offset: resultOffset,
                    operationsanmalningar: results,
                });
                commit("setTotal", {
                    total,
                    subsetName,
                });

                return {
                    results,
                    total,
                    nextPage,
                    subsetName,
                };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createOperationsanmalan: async (
            { commit, state }: BaseActionsProps<State>,
            {
                operationsanmalan,
                config = {},
            }: {
                operationsanmalan: Operationsanmalan;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const { data } = await klinikenApi.post("operation/anmalan/", operationsanmalan, getConfig(config));

                commit("addOperationsanmalningar", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    operationsanmalningar: [
                        {
                            ...data,
                            storeInfo: {
                                partial: false,
                            },
                        },
                    ],
                });

                return data.id;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateOperationsanmalan: async (
            { commit, state }: BaseActionsProps<State>,
            {
                operationsanmalan,
                frozenSubsets,
                freezeAllSubsets,
                config = {},
            }: {
                operationsanmalan: Operationsanmalan;
                config?: {
                    tabbnamn?: string;
                };
                frozenSubsets?: string[];
                freezeAllSubsets?: boolean;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `operation/anmalan/${operationsanmalan.id}/`,
                    operationsanmalan,
                    getConfig(config)
                );

                commit("updateOperationsanmalningar", {
                    frozenSubsets,
                    freezeAllSubsets,
                    subsetName: "newlyUpdated",
                    operationsanmalningar: [
                        {
                            ...response.data,
                            storeInfo: {
                                partial: false,
                            },
                        },
                    ],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });

                return response.data.id;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        partialUpdateOperationsanmalan: async (
            { commit, state }: BaseActionsProps<State>,
            {
                operationsanmalan,
                frozenSubsets,
                freezeAllSubsets,
                config = {},
            }: {
                operationsanmalan: Operationsanmalan;
                config?: {
                    tabbnamn?: string;
                };
                frozenSubsets?: string[];
                freezeAllSubsets?: boolean;
            }
        ) => {
            try {
                await klinikenApi.patch(
                    `operation/anmalan/${operationsanmalan.id}/`,
                    operationsanmalan,
                    getConfig(config)
                );

                // Hämtning görs efter patch, pga att /boka endpoint bara returnerar delar av anmälan
                const { data } = await klinikenApi.get(`operation/anmalan/${operationsanmalan.id}/`, getConfig());

                commit("updateOperationsanmalningar", {
                    frozenSubsets,
                    freezeAllSubsets,
                    subsetName: "newlyUpdated",
                    operationsanmalningar: [
                        {
                            ...data,
                            storeInfo: {
                                partial: false,
                            },
                        },
                    ],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });

                return data.id;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
